import React, { useRef, useState, useEffect, Fragment } from "react";
import { useSetRecoilState } from "recoil";
import _ from 'lodash';
import cn from "classnames";
import { TabsStyle, TabsType } from "config";
import t from "lib/i18n";
import { trackSearchTab } from "lib/tracking";
import { withConfigContext } from "hooks/useConfig";
import useModal from "hooks/useModal";
import { useGTMChange } from "hooks/useGTM";
import { favouritesFormState } from "state";
import Tickets from "components/tickets/Tickets";
import SeasonTickets from "components/season/SeasonTickets";
import OtherTickets from "components/others/OtherTickets";
import Favourites from "components/favourites/Favourites";
import FeatureFlag from "components/helpers/FeatureFlag";
import A11yElement from "components/helpers/A11yElement";
import { useSearchFormsController } from "../hooks/useSearchFormsController";
import RecentOrders from "./recentOrders/RecentOrders";
import { useFetchFavourites } from "../hooks/useFetchFavourites";
import { withCustomerData } from "../hooks/useAuth";

const Tabs = ({ tabsStyle, activeTab, disabledTabs, brand, enableBestFareFinderAndRecentOrders, apiAccessToken, apiUrl, env, customerData }) => {
  const [selected, setSelected] = useState(null);
  const setFavouritesForm = useSetRecoilState(favouritesFormState);
  const ticketsGetValuesRef = useRef(null);
  const seasonGetValuesRef = useRef(null);
  const favouritesData = useFetchFavourites({ apiAccessToken, apiUrl, brand, env, customerData });
  const favouritesGetValuesRef = useRef(null);
  const { modal } = useModal();

  const searchFormsController = useSearchFormsController();

  const parsedDisabledTabs = disabledTabs.split(",").map(tab => tab.trim());

  useEffect(() => {
    if (activeTab && !parsedDisabledTabs.includes(activeTab)) {
      setSelected(activeTab);
    } else {
      const tabsRemaining = Object.values(TabsType).filter(tab => !parsedDisabledTabs.includes(tab));
      if (tabsRemaining.length) {
        setSelected(tabsRemaining[0]);
      } else {
        console.warn("Tabs: no tabs enabled.");
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onTabChange = newSelected => () => {
    let savedValues;

    switch (selected) {
      case TabsType.TICKETS:
        savedValues = ticketsGetValuesRef.current?.();

        if (savedValues) {
         searchFormsController.tickets.setForm(savedValues);
        }
        break;

      case TabsType.SEASON:
        savedValues = seasonGetValuesRef.current?.();

        if (savedValues) {
          searchFormsController.season.setForm(savedValues);
        }
        break;

      case TabsType.FAVOURITES:
        savedValues = favouritesGetValuesRef.current?.();

        if (savedValues) {
          setFavouritesForm(savedValues);
        }
        break;

      default:
    }

    setSelected(newSelected);
  };

  useGTMChange(selected, trackSearchTab);

  // Get the function to get form's values so that we can probe forms when switching tabs.
  const onTicketsGetValues = getValues => (ticketsGetValuesRef.current = getValues);
  const onSeasonGetValues = getValues => (seasonGetValuesRef.current = getValues);
  const onFavouritesGetValues = getValues => (favouritesGetValuesRef.current = getValues);

  const baseClass = "otrl-jp__tabs";
  const classes = cn(baseClass, {
    [`${baseClass}--panel-layout`]: tabsStyle === TabsStyle.PANEL,
    [`${baseClass}--button-layout`]: tabsStyle === TabsStyle.BUTTON
  });

  return (
    <Fragment>
      <div aria-hidden={Boolean(modal)} inert={Boolean(modal)} className="otrl-jp__tabs-container">
        <div className='otrl-jp__main-wrapper'>
          <FeatureFlag name="hideNavigation" inverse={true}>
            <div className={classes}>
              {!parsedDisabledTabs.includes(TabsType.TICKETS) && (
                <A11yElement
                  onAction={onTabChange(TabsType.TICKETS)}
                  className={cn("otrl-jp__tabs__tab", {
                    "otrl-jp__tabs__tab--active": selected === TabsType.TICKETS
                  })}
                  role="tab"
                  aria-label={t("tabs.tickets.aria")}
                  aria-selected={selected === TabsType.TICKETS}
                >
                  {t("tabs.tickets")}
                </A11yElement>
              )}
              {!parsedDisabledTabs.includes(TabsType.SEASON) && (
                <A11yElement
                  onAction={onTabChange(TabsType.SEASON)}
                  className={cn("otrl-jp__tabs__tab", {
                    "otrl-jp__tabs__tab--active": selected === TabsType.SEASON
                  })}
                  role="tab"
                  aria-label={t("tabs.season.aria")}
                  aria-selected={selected === TabsType.SEASON}
                >
                  {t("tabs.season")}
                </A11yElement>
              )}
              {!parsedDisabledTabs.includes(TabsType.OTHER) && (
                <A11yElement
                  onAction={onTabChange(TabsType.OTHER)}
                  className={cn("otrl-jp__tabs__tab", {
                    "otrl-jp__tabs__tab--active": selected === TabsType.OTHER
                  })}
                  role="tab"
                  aria-label={t("tabs.other.aria")}
                  aria-selected={selected === TabsType.OTHER}
                >
                  {t("tabs.other")}
                </A11yElement>
              )}
              {(
                <A11yElement
                  onAction={onTabChange(TabsType.FAVOURITES)}
                  className={cn("otrl-jp__tabs__tab", {
                    "otrl-jp__tabs__tab--active": selected === TabsType.FAVOURITES,
                  })}
                  role="tab"
                  aria-label={t("tabs.favourites.aria")}
                  aria-selected={selected === TabsType.FAVOURITES}
                >
                  {t("tabs.favourites")}
                </A11yElement>
              )}
            </div>
          </FeatureFlag>
          {selected === TabsType.TICKETS && (
            <Tickets
              onGetValues={onTicketsGetValues}
              searchFormsController={searchFormsController}
            />
          )}
          {selected === TabsType.SEASON && (
            <SeasonTickets
              onGetValues={onSeasonGetValues}
              searchFormsController={searchFormsController}
            />
          )}
          {selected === TabsType.OTHER && <OtherTickets />}
          {selected === TabsType.FAVOURITES && <Favourites onGetValues={onFavouritesGetValues} favouritesData={favouritesData} />}
        </div>
      </div>
      {enableBestFareFinderAndRecentOrders && <RecentOrders favouritesData={favouritesData} />}
    </Fragment>
  );
};

export default _.flow(withCustomerData, withConfigContext)(Tabs);
